import { useState, useEffect, useRef } from 'react';
import Container from '@material-ui/core/Container';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import TagManager from 'react-gtm-module';
import { useLocation } from 'react-router-dom';

// @Utility
import { setStorage, getStorage, removeStorage } from "../../@utility/app/storage";
import loadLiveChat from "../../@utility/app/loadLiveChat";

// auth
import { isUserLoggedIn } from "../../@core/auth/utils";

// Hooks
import useParameterUrl from "../../@utility/hooks/useParameterUrl";

// Services
import { loadGameLikeAll } from "../../@core/services/gameService";
import { loadHomeData } from "../../@core/services/homeService";
import { loadCampAll } from "../../@core/services/gameService";

// Redux
import { setHomeData } from "../../@core/redux/actions/home/index";
import { setLoadHome, setLoadImages } from "../../@core/redux/actions/app/processing";
import { setDataCMS } from "../../@core/redux/actions/app/cms";
import { setCampAll, setGameHitAll, setCampLogoMap } from './../../@core/redux/actions/game/index';
import { loadRank } from '../../@core/redux/actions/rank';
import { loadPromotionAll } from '../../@core/redux/actions/promotion';
import { setHomeMenuType } from "../../@core/redux/actions/home/index";
import {
  setGameLikeAll
} from "../../@core/redux/actions/game";


// Routes
import VerticalRoutes from "./vertical/VerticalRoutes";

// Component
import Navbar from "./vertical/components/Navbar/index";
import FooterMenu from "./vertical/components/Footer/index";
import BrannerSlide from "./vertical/components/BrannerSlide";
import TextAnimate from "./vertical/components/TextAnimate";
import NavbarMenu from "./vertical/components/NavbarMenu";
import FooterHome from "./vertical/components/Footer";
import ListContact from "./vertical/components/ListContact";
import ConfigSet from "./vertical/components/ConfigSet";
import NotificationSubscription from "./vertical/components/NotificationSubscription";

const STORAGE_KEYS = {
  dataCMS: 'data_cms',
  allusion: 'allusion',
  notificationStatus: 'notificationStatus',
};

const safeParse = (raw) => {
  try { return JSON.parse(raw); } catch { return null; }
};

const applyCmsCssVars = (cms) => {
  if (!cms || typeof document === 'undefined') return;
  const root = document.documentElement;
  const map = {
    '--bg-card': 'bg-card',
    '--bg-foter': 'bg-foter',
    '--bg-main': 'bg-main',
    '--bg-modal': 'bg-modal',
    '--bg-navbar': 'bg-navbar',
    '--bg-popup': 'bg-popup',
    '--bg-text-animation': 'bg-text-animation',
    '--bt-confirm': 'bt-confirm',
    '--bt-danger': 'bt-danger',
    '--bt-defult': 'bt-defult',
    '--bt-info': 'bt-info',
    '--bt-login': 'bt-login',
    '--bt-register': 'bt-register',
    '--bt-secondary': 'bt-secondary',
    '--bt-sucess': 'bt-sucess',
    '--bt-undefult': 'bt-undefult',
    '--bt-warning': 'bt-warning',
  };
  Object.keys(map).forEach((cssVar) => {
    const key = map[cssVar];
    if (cms[key] != null) root.style.setProperty(cssVar, cms[key]);
  });
  if (cms['bg-foter'] && document.body) {
    document.body.style.background = cms['bg-foter'];
  }
};

const buildManifestUrl = (cms) => {
  if (!cms || typeof document === 'undefined') return null;
  const url_domain = `${window.location.protocol}//${window.location.host}`;
  const manifest = {
    short_name: cms['manifest_short_name'],
    name: cms['manifest_name'],
    icons: [
      { src: cms?.manifest_icon_xxl || '', type: 'image/webp', sizes: '512x512' },
      { src: cms?.manifest_icon_xxl_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '512x512' },
      { src: cms?.manifest_icon_xl || '', type: 'image/webp', sizes: '196x196' },
      { src: cms?.manifest_icon_xl_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '196x196' },
      { src: cms?.manifest_icon_lg || '', type: 'image/webp', sizes: '144x144' },
      { src: cms?.manifest_icon_lg_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '144x144' },
      { src: cms?.manifest_icon_md || '', type: 'image/webp', sizes: '128x128' },
      { src: cms?.manifest_icon_md_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '128x128' },
      { src: cms?.manifest_icon_sm || '', type: 'image/webp', sizes: '96x96' },
      { src: cms?.manifest_icon_sm_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '96x96' },
      { src: cms?.manifest_icon_xs || '', type: 'image/webp', sizes: '64x64' },
      { src: cms?.manifest_icon_xs_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '64x64' }
    ],
    scope: cms['manifest_scope'],
    start_url: url_domain,
    display: cms['manifest_display'],
    theme_color: cms['manifest_theme_color'],
    background_color: cms['manifest_background_color'],
  };
  const str = JSON.stringify(manifest);
  const blob = new Blob([str], { type: 'application/json' });
  return URL.createObjectURL(blob);
};

const Layouts = () => {
  const dispatch = useDispatch();

  const dataSetting = useSelector(state => state.reduxAppMenu.menu_items);
  const imagesProcessing = useSelector(state => state.reduxAppProcessing.images);
  const reduxAppCMS = useSelector(state => state.reduxAppCMS.cms);
  const homeData = useSelector(state => state.reduxHome?.result);
  const showHomeFooter = useSelector((state) => state.reduxHome.footer);
  const itemsGameLikeList = useSelector(state => state.reduxGame?.itemsGameLikeList);

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const isLargeScreenMore = useMediaQuery(theme.breakpoints.up('sm'));
  const initializedRef = useRef(false);

  const [loadRoutes, setLoadRoutes] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingNavbar, setProcessingNavbar] = useState(false);
  const [configInstallPWA, setConfigInstallPWA] = useState(null);
  const [notificationStatus, setNotificationStatus] = useState('load');
  const [notificationStatusMobile, setNotificationStatusMobile] = useState('load');
  const [checkPageGameList, setCheckPageGameList] = useState(false);
  const location = useLocation();

  const parsed = useParameterUrl();
  const { iframe_load } = parsed;
  const [iframeStatus, setIframeStatus] = useState(false);

  const gtmInitedRef = useRef(false);
  const manifestUrlRef = useRef(null);

  // const isLoggedIn = useMemo(() => isUserLoggedIn(), []);

  const setLoadDataCms = (dataCms) => {
    if (!dataCms) return;

    dispatch(setDataCMS(dataCms));
    setStorage(STORAGE_KEYS.dataCMS, JSON.stringify(dataCms));

    // DOM writes
    applyCmsCssVars(dataCms);

    // GTM one-time init
    if (dataCms['google_key'] && !gtmInitedRef.current) {
      TagManager.initialize({ gtmId: dataCms['google_key'] });
      gtmInitedRef.current = true;
    }

    // PWA manifest links
    const manifestUrl = buildManifestUrl(dataCms);
    if (manifestUrlRef.current) {
      URL.revokeObjectURL(manifestUrlRef.current);
    }
    manifestUrlRef.current = manifestUrl;

    const manifestEl = document.querySelector('#manifest-project');
    const appleEl = document.querySelector('#apple-touch-project');
    const iconRelEl = document.querySelector('#icon-rel');
    if (manifestEl && manifestUrl) manifestEl.setAttribute('href', manifestUrl);
    if (appleEl && dataCms['manifest_icon_lg']) appleEl.setAttribute('href', dataCms['manifest_icon_lg']);
    if (iconRelEl && dataCms['rel_icon_big']) iconRelEl.setAttribute('href', dataCms['rel_icon_big']);

    // PWA Notification config
    const url_domain = `${window.location.protocol}//${window.location.host}`;

    if (dataCms['section_notificaion_pwa'] === 1 || dataCms['section_notificaion_pwa'] === '1') {
      setConfigInstallPWA({
        title: `แอพ ${dataCms['manifest_name']}`,
        body: `คลิกเพื่อติดตั้งแอพ ${dataCms['manifest_name']}`,
        text: dataCms['title_notificaion_pwa'],
        icon: dataCms['manifest_icon_lg'],
        data: { url: url_domain },
      });
      const n = getStorage(STORAGE_KEYS.notificationStatus);
      setNotificationStatus(n);
    } else {
      setNotificationStatus('load');
    }

    if (dataCms['section_notificaion_mobile_pwa'] === 1 || dataCms['section_notificaion_mobile_pwa'] === '1') {
      setConfigInstallPWA({
        title: `แอพ ${dataCms['manifest_name']}`,
        body: `คลิกเพื่อติดตั้งแอพ ${dataCms['manifest_name']}`,
        text: dataCms['title_notificaion_pwa'],
        icon: dataCms['manifest_icon_lg'],
        data: { url: url_domain },
      });
      setNotificationStatusMobile(true);
    }
  };

  // mount
  useEffect(() => {
    setStorage(STORAGE_KEYS.allusion, document.referrer);
    setLoadRoutes(true);

    const pathData = window.location.pathname;
    if (pathData !== '/') {
      dispatch(setLoadHome(true));
      dispatch(setLoadImages(true));
    }

    if (isUserLoggedIn()) {
      loadHomeData().then((data) => {
        if (data?.code === 0 || data?.code === 200) {
          dispatch(setHomeData(data.result));
        }
      });
    }
  }, [dispatch, isUserLoggedIn()]);

  // CMS cache-first + Redux
  useEffect(() => {
    // Redux มาก่อน
    if (dataSetting?.cms != null) {
      removeStorage(STORAGE_KEYS.dataCMS);
      setLoadDataCms(dataSetting.cms);
      setProcessing(true);
      setProcessingNavbar(true);
      return;
    }

    // ถ้า Redux ยังไม่มี → ใช้ cache
    const raw = getStorage(STORAGE_KEYS.dataCMS);
    if (raw) {
      const cached = safeParse(raw);
      if (cached) {
        setLoadDataCms(cached);
        setProcessingNavbar(true);
        // รอ Redux setting มาก่อนค่อย setProcessing(true) เพื่อให้จังหวะโหลด camp ตรง
        return;
      }
    }

    // ไม่มีทั้งคู่
    setProcessingNavbar(false);
  }, [dataSetting?.cms]);

  // Live chat
  useEffect(() => {
    if (!dataSetting) return;
    const contacts = dataSetting?.agent_contact;
    if (Array.isArray(contacts) && contacts.length > 0) {
      const live = contacts.find((item) => item.contact_channel === 1);
      if (live?.channel_appid) loadLiveChat(live.channel_appid);
    }
  }, [dataSetting]);

  // iframe flag
  useEffect(() => {
    setIframeStatus(iframe_load === true || iframe_load === 'true');
  }, [iframe_load]);

  // load camp when ready
  useEffect(() => {
    if (!processing) return;
    loadCampAll(99, true).then((result) => {
      dispatch(setCampAll(result));
      dispatch(setCampLogoMap(result));
    });
  }, [dispatch, processing]);

  // images start → ranks & promos
  useEffect(() => {
    if (!imagesProcessing) return;
    if (reduxAppCMS) {
      if (reduxAppCMS?.section_games_rank === '1' || reduxAppCMS?.section_games_rank === 1) {
        dispatch(setGameHitAll());
      }
      if (reduxAppCMS?.section_user_rank === '1' || reduxAppCMS?.section_user_rank === 1) {
        dispatch(loadRank());
      }
    }
    dispatch(loadPromotionAll());
  }, [dispatch, reduxAppCMS, imagesProcessing]);

  // default menu from menu_camp
  useEffect(() => {
    const menuArr = dataSetting?.menu_camp;
    if (Array.isArray(menuArr) && menuArr.length > 0) {
      const item = menuArr[0];
      dispatch(setHomeMenuType({
        id: 0,
        renderPage: item.code,
        pageTitle: item.title,
        game_camp_image: item.game_camp_image,
        column: {
          desktop: item.column_desktop,
          mobile: item.column_mobile
        },
        typeGame: item.game_camp_type
      }));
    }
  }, [dispatch, dataSetting?.menu_camp]);

  // cleanup blob url
  useEffect(() => {
    return () => {
      if (manifestUrlRef.current) URL.revokeObjectURL(manifestUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (homeData && !initializedRef.current && !itemsGameLikeList) {
      initializedRef.current = true;

      loadGameLikeAll().then((result) => {
        dispatch(setGameLikeAll(result ? result : []));
      });
    }
  }, [homeData]);

  return (
    <>
      {loadRoutes && (
        iframeStatus ? (
          <Container maxWidth="lg" className="bgContainer">
            <VerticalRoutes
              dataSetting={dataSetting}
              setCheckPageGameList={setCheckPageGameList}
              checkPageGameList={checkPageGameList}
            />

            <ConfigSet
              loadRoutes={loadRoutes}
              processing={processing}
              isUserLoggedIn={isUserLoggedIn}
            />
          </Container>
        ) : location.pathname.startsWith("/open-game/lottery") ? (
          <>
            <VerticalRoutes
              dataSetting={dataSetting}
              setCheckPageGameList={setCheckPageGameList}
              checkPageGameList={checkPageGameList}
            />

            <ConfigSet
              loadRoutes={loadRoutes}
              processing={processing}
              isUserLoggedIn={isUserLoggedIn}
            />
          </>
        ) : location.pathname === "/live-chat" ? (
          <>
            <VerticalRoutes
              dataSetting={dataSetting}
              setCheckPageGameList={setCheckPageGameList}
              checkPageGameList={checkPageGameList}
            />

            <ConfigSet
              loadRoutes={loadRoutes}
              processing={processing}
              isUserLoggedIn={isUserLoggedIn}
            />
          </>
        ) : (
          <>
            {processingNavbar && (
              <Navbar
                isUserLoggedIn={isUserLoggedIn()}
                isLargeScreen={isLargeScreenMore}
                notificationStatus={
                  notificationStatusMobile === null ||
                  notificationStatusMobile === undefined ||
                  notificationStatusMobile === ""
                }
                setNotificationStatusMobile={setNotificationStatusMobile}
                configInstallPWA={configInstallPWA}
              />
            )}

            <div className="homePosition">
              <BrannerSlide isLargeScreen={isLargeScreen} />
              <TextAnimate />
            </div>

            {(homeData || isUserLoggedIn()) && (
              <NavbarMenu
                isUserLoggedIn={isUserLoggedIn()}
                isLargeScreen={isLargeScreen}
                checkPageGameList={checkPageGameList}
              />
            )}

            <Container maxWidth="lg" className="bgContainer">
              <div className={isUserLoggedIn() ? "wrapperActive" : "wrapper"}>
                {loadRoutes && (
                  <VerticalRoutes
                    dataSetting={dataSetting}
                    setCheckPageGameList={setCheckPageGameList}
                    checkPageGameList={checkPageGameList}
                  />
                )}

                {processing && imagesProcessing && showHomeFooter && (
                  <div className="cardMargin relative">
                    <div className="left">
                      <ListContact isLargeScreen={isLargeScreen} />
                    </div>

                    <div className="cardMargin relative" style={{ marginTop: "0px" }}>
                      <div className="left">
                        <FooterHome />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Container>

            {processingNavbar && (
              <FooterMenu
                isUserLoggedIn={isUserLoggedIn()}
                isLargeScreen={isLargeScreen}
              />
            )}

            <ConfigSet
              loadRoutes={loadRoutes}
              processing={processing}
              isUserLoggedIn={isUserLoggedIn}
            />

            {notificationStatus !== "load" &&
              isLargeScreenMore &&
              (notificationStatus === null ||
                notificationStatus === undefined ||
                notificationStatus === "") &&
              configInstallPWA && (
                <NotificationSubscription
                  setStorage={setStorage}
                  configInstallPWA={configInstallPWA}
                  notificationStatus={
                    notificationStatus === null ||
                    notificationStatus === undefined ||
                    notificationStatus === ""
                  }
                />
              )}
          </>
        )
      )}
    </>
  );

};

export default Layouts;
